/**
 * Lesson block on a Knowledge article: the client port of the backend
 * `Knowledge._clean_lesson` (docs/LMS_CONTRACT.md §3).
 *
 * The server owns the saved shape, so this exists for the editor to show what a
 * save will keep, for the mock backend, and to vet questions a model generated
 * before they reach the form. Question ids become the keys trainers' answers
 * are graded under, so the two implementations must stay identical.
 */

import { isSlug } from './sopSteps';
import { MAX_MEDIA, normalizeMedia } from './mediaLinks';

// Re-exported so the editor keeps one import for everything a lesson block holds.
export { MAX_MEDIA, MAX_MEDIA_URL, MEDIA_HOSTS, MEDIA_KINDS, isAllowedMediaUrl, mediaHost } from './mediaLinks';

export const LESSON_KINDS = ['read', 'video', 'hands_on', 'external_cert', 'signoff'];

/** One wording for a lesson kind, the learner's, in the player, the outline, the editor and the course builder. */
export const LESSON_KIND_LABELS = {
    read: 'Read',
    video: 'Watch',
    hands_on: 'Hands-on',
    external_cert: 'Certificate',
    signoff: 'Sign-off',
};

/** Mirror of `Constants.CERT_KINDS`; the label is what the trainer sees. */
export const CERT_KINDS = {
    cpr_aed: { label: 'CPR / AED', validDays: 730 },
    harassment: { label: 'Harassment prevention', validDays: null },
};

/** Mirror of `Constants.ROLES`: what a course's `requiredFor` may name. */
export const ROLES = ['HeroTrainer', 'Manager', 'Admin'];

/** How a module is passed (`LearningAdmin._clean_module`); the labels reuse the lesson-kind words. */
export const COMPLETIONS = ['quiz', 'hands_on', 'external_cert', 'signoff'];
export const COMPLETION_LABELS = {
    quiz: 'Quiz',
    hands_on: LESSON_KIND_LABELS.hands_on,
    external_cert: LESSON_KIND_LABELS.external_cert,
    signoff: LESSON_KIND_LABELS.signoff,
};

export const FLOW_KINDS = ['open', 'close', 'cleaning'];

export const PASS_SCORE_DEFAULT = 0.8;
export const PASS_SCORE_MIN = 0.5;

export const MAX_OBJECTIVES = 8;
export const MAX_OBJECTIVE = 200;
export const MAX_FORMAT = 120;
export const MAX_MINUTES = 600;
export const MAX_FLOW_KIND = 200;
export const MAX_QUESTIONS = 30;
export const MAX_PROMPT = 500;
export const MIN_CHOICES = 2;
export const MAX_CHOICES = 6;
export const MAX_CHOICE = 200;
export const MAX_EXPLANATION = 500;

// An article id, not a slug: seeded ids are slugs but a hand-made one may carry
// capitals, so only whitespace and the `sop:` separator itself are refused.
const SOP_ID_RE = /^[^\s:]+$/;

const text = (value, max) => String(value ?? '').trim().slice(0, max);

/**
 * Mirror of the backend `_whole_int`: an integer, or a string of digits
 * (signed, padding allowed), as a number; anything else is null. Booleans,
 * fractions and `'5 min'` are not whole numbers, so `minutes` falls back to 0
 * and a question with such an `answer` is dropped rather than guessed at.
 */
export const wholeInt = (raw) => {
    if (typeof raw === 'boolean') return null;
    if (typeof raw === 'number') return Number.isInteger(raw) ? raw : null;
    if (typeof raw === 'string' && /^-?\d+$/.test(raw.trim())) return Number.parseInt(raw.trim(), 10);
    return null;
};

const cleanObjectives = (raw) => {
    if (!Array.isArray(raw)) return [];
    return raw
        .map((item) => text(item, MAX_OBJECTIVE))
        .filter(Boolean)
        .slice(0, MAX_OBJECTIVES);
};

const cleanMinutes = (raw) => {
    const n = wholeInt(raw);
    if (n === null) return 0;
    return Math.min(MAX_MINUTES, Math.max(0, n));
};

const cleanPassScore = (raw) => {
    // Only a number or a numeric string is a score; a boolean or anything else is the default.
    const n = typeof raw === 'number' ? raw : typeof raw === 'string' && raw.trim() ? Number(raw) : NaN;
    if (!Number.isFinite(n)) return PASS_SCORE_DEFAULT;
    return Math.min(1, Math.max(PASS_SCORE_MIN, n));
};

/**
 * One question without its id, or null when it cannot be graded. An empty
 * choice is dropped and the answer follows the choice it pointed at, so a blank
 * row in the editor never silently moves the correct answer. More than six
 * choices are cut to six; the question stays only if its answer survived the cut.
 */
const cleanQuestion = (raw) => {
    if (!raw || typeof raw !== 'object') return null;
    const prompt = text(raw.prompt, MAX_PROMPT);
    if (!prompt) return null;
    const rawChoices = Array.isArray(raw.choices) ? raw.choices : [];
    const answer = wholeInt(raw.answer);
    if (answer === null || answer < 0 || answer >= rawChoices.length) return null;

    const compacted = [];
    let answerIndex = -1;
    rawChoices.forEach((choice, i) => {
        const cleaned = text(choice, MAX_CHOICE);
        if (!cleaned) return;
        if (i === answer) answerIndex = compacted.length;
        compacted.push(cleaned);
    });
    const choices = compacted.slice(0, MAX_CHOICES);
    if (choices.length < MIN_CHOICES || answerIndex < 0 || answerIndex >= choices.length) return null;

    return {
        prompt,
        choices,
        answer: answerIndex,
        explanation: text(raw.explanation, MAX_EXPLANATION),
    };
};

/**
 * The first 30 gradable questions, with ids unique within the lesson. Every
 * valid slug id is reserved before any default is handed out, so inserting a
 * question ahead of `q2` cannot steal `q2` from the question whose attempts are
 * keyed on it. A missing, invalid or duplicate id takes the next free `q<n>`
 * (a number, so a collision reads as the next number, never `q1-2`).
 */
export const normalizeQuestions = (raw) => {
    if (!Array.isArray(raw)) return [];
    const kept = [];
    raw.forEach((item) => {
        if (kept.length >= MAX_QUESTIONS) return;
        const cleaned = cleanQuestion(item);
        if (cleaned) kept.push({ cleaned, id: isSlug(item.id) ? item.id : null });
    });

    const reserved = new Set(kept.map(({ id }) => id).filter(Boolean));
    const used = new Set();
    return kept.map(({ cleaned, id }, i) => {
        let questionId;
        if (id && reserved.has(id)) {
            reserved.delete(id);
            questionId = id;
        } else {
            let n = i + 1;
            while (used.has(`q${n}`) || reserved.has(`q${n}`)) n += 1;
            questionId = `q${n}`;
        }
        used.add(questionId);
        return { id: questionId, ...cleaned };
    });
};

const cleanKnowledgeCheck = (raw) => {
    const source = raw && typeof raw === 'object' ? raw : {};
    return {
        passScore: cleanPassScore(source.passScore),
        questions: normalizeQuestions(source.questions),
    };
};

export const cleanFlowKind = (raw) => {
    if (typeof raw !== 'string') return null;
    const value = text(raw, MAX_FLOW_KIND);
    if (FLOW_KINDS.includes(value)) return value;
    if (value.startsWith('sop:') && SOP_ID_RE.test(value.slice(4))) return value;
    return null;
};

const cleanHandsOn = (raw) => {
    const source = raw && typeof raw === 'object' ? raw : {};
    // Cosign defaults on: a walk nobody watched is what the manager confirmation
    // exists for, so opting out is the explicit choice.
    return { flowKind: cleanFlowKind(source.flowKind), cosign: source.cosign === undefined ? true : Boolean(source.cosign) };
};

const cleanCertKind = (raw) => (Object.keys(CERT_KINDS).includes(raw) ? raw : 'cpr_aed');

/** Mirror of `Knowledge._clean_lesson`: null when there is no lesson to keep. */
export const normalizeLesson = (raw) => {
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
    // A block with no kind is the editor's "no lesson", not a read lesson with defaults.
    if (typeof raw.kind !== 'string' || !raw.kind.trim()) return null;
    const kind = LESSON_KINDS.includes(raw.kind) ? raw.kind : 'read';

    const lesson = {
        kind,
        objectives: cleanObjectives(raw.objectives),
        format: text(raw.format, MAX_FORMAT),
        minutes: cleanMinutes(raw.minutes),
        media: normalizeMedia(raw.media, MAX_MEDIA),
        knowledgeCheck: cleanKnowledgeCheck(raw.knowledgeCheck),
    };
    if (kind === 'hands_on') lesson.handsOn = cleanHandsOn(raw.handsOn);
    if (kind === 'external_cert') lesson.certKind = cleanCertKind(raw.certKind);
    return lesson;
};

export const emptyLesson = (kind = 'read') => normalizeLesson({ kind });

export const emptyQuestion = () => ({
    id: '',
    prompt: '',
    choices: ['', ''],
    answer: 0,
    explanation: '',
});
